import React, { useRef, useState, useEffect } from 'react';
import { InboxOutlined, RotateRightOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button } from 'antd';

const MoodboardCanvas = ({
    items = [],
    onItemsChange, // Changed name to imply local update
    onSave, // New prop for DB persistence
    onDropFiles,
    readOnly = false,
    width,
    height,
    isUploadActive = false // To show upload state visuals
}) => {
    const containerRef = useRef(null);
    const [selectedId, setSelectedId] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isRotating, setIsRotating] = useState(false);
    const [rotateStartAngle, setRotateStartAngle] = useState(0);
    const [initialItemRotation, setInitialItemRotation] = useState(0);

    // Track if we actually moved something to avoid saving on simple clicks
    const hasMovedRef = useRef(false);

    // Deselect when clicking empty area
    const handleContainerClick = (e) => {
        if (e.target === containerRef.current) {
            setSelectedId(null);
        }
    };

    // --- Drag & Drop for Files ---
    const handleFileDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (readOnly) return;

        const files = [...e.dataTransfer.files];
        if (files.length > 0 && onDropFiles) {
            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            onDropFiles(files, { x, y });
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // --- Item Interaction: Move ---
    const handleItemMouseDown = (e, item) => {
        if (readOnly) return;
        e.stopPropagation();
        setSelectedId(item.id);
        setIsDragging(true);
        hasMovedRef.current = false;

        // Calculate offset from item's top-left
        setDragOffset({
            x: e.clientX - (containerRef.current.getBoundingClientRect().left + item.x),
            y: e.clientY - (containerRef.current.getBoundingClientRect().top + item.y)
        });
    };

    // --- Item Interaction: Rotate ---
    const handleRotateMouseDown = (e, item) => {
        if (readOnly) return;
        e.stopPropagation();
        setIsRotating(true);
        setSelectedId(item.id); // Ensure selected
        hasMovedRef.current = false;

        const rect = containerRef.current.getBoundingClientRect();
        // Item center
        const cx = rect.left + item.x + item.width / 2;
        const cy = rect.top + item.y + (item.height || 150) / 2; // Approximate height if auto, but usually we set it

        // Angle to mouse
        const angle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
        setRotateStartAngle(angle);
        setInitialItemRotation(item.rotation || 0);
    };

    // --- Global Mouse Move / Up ---
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();

            if (isDragging && selectedId) {
                hasMovedRef.current = true;
                const x = e.clientX - rect.left - dragOffset.x;
                const y = e.clientY - rect.top - dragOffset.y;

                // Update item position locally in parent via callback
                const updatedItems = items.map(it =>
                    it.id === selectedId ? { ...it, x, y } : it
                );
                if (onItemsChange) onItemsChange(updatedItems);
            }

            if (isRotating && selectedId) {
                hasMovedRef.current = true;
                const item = items.find(it => it.id === selectedId);
                if (item) {
                    const cx = rect.left + item.x + item.width / 2;
                    const cy = rect.top + item.y + (item.height || 150) / 2; // Assuming aspect ratio or fixed height behavior
                    const currentAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
                    const delta = currentAngle - rotateStartAngle;

                    const updatedItems = items.map(it =>
                        it.id === selectedId ? { ...it, rotation: initialItemRotation + delta } : it
                    );
                    if (onItemsChange) onItemsChange(updatedItems);
                }
            }
        };

        const handleMouseUp = () => {
            if ((isDragging || isRotating) && hasMovedRef.current) {
                // Interaction ended and changes occurred -> Trigger Save
                if (onSave) {
                    onSave(items); // Pass current state items to save
                }
            }
            setIsDragging(false);
            setIsRotating(false);
            hasMovedRef.current = false;
        };

        if (isDragging || isRotating) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isRotating, selectedId, items, onItemsChange, onSave, dragOffset, rotateStartAngle, initialItemRotation]);

    // --- Keyboard Delete ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedId && (e.key === 'Delete' || e.key === 'Backspace')) {
                const updatedItems = items.filter(it => it.id !== selectedId);
                // Delete is instantaneous, so valid to save immediately or follow pattern
                if (onItemsChange) onItemsChange(updatedItems);
                if (onSave) onSave(updatedItems);
                setSelectedId(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedId, items, onItemsChange, onSave]);


    return (
        <div
            ref={containerRef}
            className="nodrag" // IMPORTANT for React Flow
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                // If it's the node view, background is handled by parent usually, but expanded view needs it
                // We'll trust parent passes correct style container or we just render items transparently
            }}
            onDrop={handleFileDrop}
            onDragOver={handleDragOver}
            onMouseDown={handleContainerClick}
        >
            {/* Empty State */}
            {items.length === 0 && !isUploadActive && (
                <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(255,255,255,0.3)', pointerEvents: 'none'
                }}>
                    <InboxOutlined style={{ fontSize: 48, marginBottom: 8 }} />
                    <span>Drag items here</span>
                </div>
            )}

            {/* Items */}
            {items.map(item => {
                const isSelected = item.id === selectedId;

                return (
                    <div
                        key={item.id}
                        style={{
                            position: 'absolute',
                            left: item.x,
                            top: item.y,
                            width: item.width,
                            // Ensure height is auto or fixed? Moodboard images usually fix width, height auto
                            transform: `rotate(${item.rotation || 0}deg)`,
                            zIndex: item.zIndex || 1,
                            cursor: readOnly ? 'default' : (isDragging && isSelected ? 'grabbing' : 'grab'),
                            transition: isDragging && isSelected ? 'none' : 'box-shadow 0.2s',
                        }}
                        onMouseDown={(e) => handleItemMouseDown(e, item)}
                    >
                        <div style={{
                            position: 'relative',
                            width: '100%',
                            boxShadow: isSelected ? '0 0 0 2px #1890ff, 0 8px 16px rgba(0,0,0,0.3)' : '0 4px 8px rgba(0,0,0,0.2)',
                            transition: 'box-shadow 0.2s'
                        }}>
                            <img
                                src={item.src}
                                alt="mood-item"
                                style={{ width: '100%', height: 'auto', display: 'block', pointerEvents: 'none', border: '2px solid white' }}
                            />

                            {/* Controls Overlay when selected */}
                            {isSelected && !readOnly && (
                                <>
                                    {/* Rotation Handle */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: -30,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: 24,
                                            height: 24,
                                            background: '#fff',
                                            borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                            zIndex: 10
                                        }}
                                        onMouseDown={(e) => handleRotateMouseDown(e, item)}
                                    >
                                        <RotateRightOutlined style={{ fontSize: 12, color: '#333' }} />
                                    </div>

                                    {/* Connector line to rotation handle */}
                                    <div style={{
                                        position: 'absolute', top: -10, left: '50%', width: 1, height: 10, background: '#1890ff'
                                    }} />

                                    {/* Simple delete button corner */}
                                    {/* <div
                                        style={{
                                            position: 'absolute',
                                            top: -10,
                                            right: -10,
                                            width: 20,
                                            height: 20,
                                            background: '#ff4d4f',
                                            borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer',
                                            zIndex: 11
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const updated = items.filter(i => i.id !== item.id);
                                            onUpdateItems(updated);
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()} // Prevent drag start
                                     >
                                        <DeleteOutlined style={{ fontSize: 10, color: '#fff' }} />
                                     </div> */}
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default MoodboardCanvas;
